import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';

import FaqHeader from '../../components/Qna/Faq/FaqHeader';
import FaqSide from '../../components/Qna/Faq/FaqContent/FaqSide';
import FaqList from '../../components/Qna/Faq/FaqContent/FaqList';
import { getTopicsWithFaqs } from '../../api/faq/faq.api';

export const Faq = () => {
  const location = useLocation();
  const targetSlug = location.state?.targetSlug ?? null;
  const targetFaqId = location.state?.targetFaqId ?? null;

  const [topics, setTopics] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [openFaqId, setOpenFaqId] = useState(null);
  const sectionRefs = useRef({});
  const faqItemRefs = useRef({});
  const hasScrolled = useRef(false);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const data = await getTopicsWithFaqs();
        setTopics(data);
        if (data.length > 0) {
          setSelectedSlug(targetSlug ?? data[0].slug);
        }
      } catch (error) {
        console.error('Failed to fetch FAQ topics:', error);
      }
    };
    fetchTopics();
  }, [targetSlug]);

  useEffect(() => {
    if (!targetFaqId || hasScrolled.current) return;

    const el = faqItemRefs.current[targetFaqId];
    if (el) {
      setOpenFaqId(targetFaqId);
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      if (targetSlug) setSelectedSlug(targetSlug);
      hasScrolled.current = true;
    }
  }, [targetFaqId, targetSlug, topics]);

  useEffect(() => {
    if (topics.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setSelectedSlug(visible[0].target.dataset.slug);
        }
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 },
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [topics]);

  const handleSelect = useCallback((slug) => {
    const el = sectionRefs.current[slug];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const latestUpdated = useMemo(() => {
    if (topics.length === 0) return null;
    return topics
      .map((topic) => new Date(topic.lastUpdated))
      .reduce((max, cur) => (cur > max ? cur : max));
  }, [topics]);

  return (
    <div className='bg-grayscale-gray01 min-h-screen w-full flex flex-col items-center text-center pb-[400px]'>
      <FaqHeader lastUpdated={latestUpdated} />
      <div className='flex px-20 justify-center items-start gap-6 w-full'>
        <div className='sticky top-0 self-start py-20'>
          <FaqSide
            selectedSlug={selectedSlug}
            onSelect={handleSelect}
            topics={topics}
          />
        </div>
        <FaqList
          topics={topics}
          sectionRefs={sectionRefs}
          faqItemRefs={faqItemRefs}
          openFaqId={openFaqId}
        />
      </div>
    </div>
  );
};

export default Faq;
